import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Wrench, Search, Plus, RotateCcw, User, Package,
  CheckCircle, MapPin, Clock, History, ChevronDown,
  Download, Filter, ArrowLeftRight, Loader2, UserPlus, QrCode, Printer, FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';
import { useRegisterCmdKCommands } from '../../contexts/CmdKContext';
import { exportToCSV, printTable } from '../../lib/printUtils';

export default function HerramientasCargoPage() {
  const [tab, setTab] = useState('cargo'); // cargo | asignar | ubicacion | historial
  const [enCargo, setEnCargo] = useState([]);
  const [porPersona, setPorPersona] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  // Assign form
  const [herramientaSearch, setHerramientaSearch] = useState('');
  const [herramientaResults, setHerramientaResults] = useState([]);
  const [selectedHerramienta, setSelectedHerramienta] = useState(null);
  const [showHerResults, setShowHerResults] = useState(false);
  const [trabSearch, setTrabSearch] = useState('');
  const [trabResults, setTrabResults] = useState([]);
  const [selectedTrab, setSelectedTrab] = useState(null);
  const [showTrabResults, setShowTrabResults] = useState(false);
  const [otSearch, setOtSearch] = useState('');
  const [otResults, setOtResults] = useState([]);
  const [showOtResults, setShowOtResults] = useState(false);
  const [selectedOt, setSelectedOt] = useState('');
  const [cantidad, setCantidad] = useState(1);
  const [ubicacion, setUbicacion] = useState('A CARGO');
  const [obs, setObs] = useState('');
  const [sending, setSending] = useState(false);

  // Fix 2: Ubicaciones dinámicas (catálogo)
  const [ubicaciones, setUbicaciones] = useState([]); // [{id, nombre}]
  const [showUbicPicker, setShowUbicPicker] = useState(false);
  const [creatingUbic, setCreatingUbic] = useState(false);
  const ubicRef = useRef(null);

  // Ubicación (B1)
  const [ubicQuery, setUbicQuery] = useState('');
  const [ubicResultados, setUbicResultados] = useState([]);
  const [ubicLoading, setUbicLoading] = useState(false);
  const [ubicSearched, setUbicSearched] = useState(false);

  // Historial filtros (B3)
  const [hFechaDesde, setHFechaDesde] = useState('');
  const [hFechaHasta, setHFechaHasta] = useState('');
  const [hTrabajador, setHTrabajador] = useState('');
  const [hOt, setHOt] = useState('');
  const [hCodigo, setHCodigo] = useState('');
  const [hEstado, setHEstado] = useState('');

  // Reasignar (B2)
  const [reasigAsig, setReasigAsig] = useState(null); // asignacion activa
  const [reasigTrabSearch, setReasigTrabSearch] = useState('');
  const [reasigTrabResults, setReasigTrabResults] = useState([]);
  const [reasigSelectedTrab, setReasigSelectedTrab] = useState(null);
  const [reasigOt, setReasigOt] = useState('');
  const [reasigObs, setReasigObs] = useState('');
  const [reasigUbicacion, setReasigUbicacion] = useState('en_cargo');
  const [reasigSending, setReasigSending] = useState(false);

  const herRef = useRef(null);
  const trabRef = useRef(null);
  const otRef = useRef(null);

  // Deep-link ?q=X&tab=ubicacion → auto-ejecuta búsqueda (para QR)
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const qParam = searchParams.get('q');
    const tabParam = searchParams.get('tab');
    if (tabParam === 'ubicacion' && qParam) {
      setTab('ubicacion');
      setUbicQuery(qParam);
      // Trigger búsqueda tras render
      setTimeout(() => {
        (async () => {
          setUbicLoading(true); setUbicSearched(true);
          try {
            const res = await api.get('/bodega/herramientas/ubicacion', { params: { q: qParam } });
            setUbicResultados(res.data?.items || []);
          } catch {
            setUbicResultados([]);
          } finally { setUbicLoading(false); }
        })();
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEnCargo = useCallback(async () => {
    setLoading(true);
    try {
      const params = searchFilter ? `?trabajador=${encodeURIComponent(searchFilter)}` : '';
      const res = await api.get(`/bodega/herramientas/en-cargo${params}`);
      setEnCargo(res.data.en_cargo || []);
      setPorPersona(res.data.por_persona || []);
    } catch { }
    finally { setLoading(false); }
  }, [searchFilter]);

  const loadHistorial = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (hFechaDesde) params.fecha_desde = hFechaDesde;
      if (hFechaHasta) params.fecha_hasta = hFechaHasta;
      if (hTrabajador) params.trabajador = hTrabajador;
      if (hOt) params.ot = hOt;
      if (hCodigo) params.codigo = hCodigo;
      if (hEstado) params.estado = hEstado;
      const res = await api.get('/bodega/herramientas/historial-filtrado', { params });
      setHistorial(res.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando historial');
    } finally { setLoading(false); }
  }, [hFechaDesde, hFechaHasta, hTrabajador, hOt, hCodigo, hEstado]);

  const exportCSVHistorial = async () => {
    try {
      const params = new URLSearchParams({ formato: 'csv', limit: '2000' });
      if (hFechaDesde) params.append('fecha_desde', hFechaDesde);
      if (hFechaHasta) params.append('fecha_hasta', hFechaHasta);
      if (hTrabajador) params.append('trabajador', hTrabajador);
      if (hOt) params.append('ot', hOt);
      if (hCodigo) params.append('codigo', hCodigo);
      if (hEstado) params.append('estado', hEstado);
      const res = await api.get(`/bodega/herramientas/historial-filtrado?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `historial_herramientas_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV descargado');
    } catch (err) {
      toast.error('Error al exportar');
    }
  };

  const buscarUbicacion = useCallback(async () => {
    const qs = (ubicQuery || '').trim();
    if (qs.length < 2) { toast.error('Escribí al menos 2 caracteres'); return; }
    setUbicLoading(true);
    setUbicSearched(true);
    try {
      const res = await api.get('/bodega/herramientas/ubicacion', { params: { q: qs } });
      setUbicResultados(res.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al buscar ubicación');
      setUbicResultados([]);
    } finally { setUbicLoading(false); }
  }, [ubicQuery]);

  // Descarga PDF de etiquetas QR (1 o varias herramientas)
  const descargarQR = async (codigos) => {
    const list = Array.isArray(codigos) ? codigos : [codigos];
    if (list.length === 0) return;
    try {
      const baseUrl = window.location.origin;
      const res = await api.get('/bodega/herramientas/qr-etiquetas', {
        params: { codigos: list.join(','), base_url: baseUrl },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `etiquetas_qr_${list.length}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`PDF con ${list.length} etiqueta${list.length > 1 ? 's' : ''} descargado`);
    } catch {
      toast.error('Error al generar PDF de etiquetas');
    }
  };

  // Cargar ubicaciones una vez
  const loadUbicaciones = useCallback(async () => {
    try {
      const res = await api.get('/bodega/ubicaciones?activas_solo=true');
      setUbicaciones(res.data?.items || []);
    } catch { /* empty */ }
  }, []);
  useEffect(() => { loadUbicaciones(); }, [loadUbicaciones]);

  // Crear nueva ubicación inline
  const crearUbicacion = async (nombre) => {
    const clean = (nombre || '').trim();
    if (!clean) return null;
    setCreatingUbic(true);
    try {
      const res = await api.post('/bodega/ubicaciones', { nombre: clean });
      await loadUbicaciones();
      const nuevoNombre = res.data?.nombre || clean.toUpperCase();
      toast.success(res.data?.reactivada ? `Ubicación "${nuevoNombre}" reactivada` : `Ubicación "${nuevoNombre}" creada`);
      return nuevoNombre;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al crear ubicación');
      return null;
    } finally {
      setCreatingUbic(false);
    }
  };

  useEffect(() => { if (tab === 'cargo') loadEnCargo(); }, [tab, loadEnCargo]);
  useEffect(() => { if (tab === 'historial') loadHistorial(); }, [tab, loadHistorial]);

  // Carlos (10/05/2026): imprimir y exportar herramientas según tab activo
  const imprimirHerramientas = () => {
    const isHist = tab === 'historial';
    const rows = isHist ? historial : enCargo;
    if (!rows.length) { toast.warning(`No hay datos en "${isHist ? 'Historial' : 'En Cargo'}" para imprimir`); return; }
    printTable({
      title: `Herramientas a Cargo · ${isHist ? 'Historial' : 'En Cargo'}`,
      subtitle: `${rows.length} registros · ${new Date().toLocaleString('es-CL')}`,
      summary: [
        { label: 'Tab', value: isHist ? 'Historial' : 'En Cargo' },
        { label: 'Registros', value: rows.length },
      ],
      columns: isHist ? [
        { key: 'fecha', label: 'Fecha', format: (v) => v ? new Date(v).toLocaleString('es-CL') : '' },
        { key: 'tipo_movimiento', label: 'Movimiento', align: 'center' },
        { key: 'herramienta_codigo', label: 'Cód. Herram.', mono: true },
        { key: 'herramienta_descripcion', label: 'Descripción' },
        { key: 'cantidad', label: 'Cant.', align: 'right' },
        { key: 'trabajador_nombre', label: 'Trabajador' },
        { key: 'ot_destino', label: 'OT' },
        { key: 'ubicacion', label: 'Ubicación' },
        { key: 'usuario_responsable', label: 'Resp.' },
      ] : [
        { key: 'codigo', label: 'Código', mono: true },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'cantidad', label: 'Cant.', align: 'right' },
        { key: 'trabajador_nombre', label: 'Trabajador' },
        { key: 'ot_destino', label: 'OT', align: 'center' },
        { key: 'ubicacion', label: 'Ubicación' },
        { key: 'fecha_asignacion', label: 'Asignada', format: (v) => v ? new Date(v).toLocaleDateString('es-CL') : '' },
        { key: 'observaciones', label: 'Observaciones' },
      ],
      rows,
    });
  };

  const exportarHerramientasExcel = () => {
    const isHist = tab === 'historial';
    const rows = isHist ? historial : enCargo;
    if (!rows.length) { toast.warning('No hay datos para exportar'); return; }
    exportToCSV({
      filename: `herramientas_${isHist ? 'historial' : 'en_cargo'}`,
      title: `CHIRINO STEEL · Herramientas · ${isHist ? 'Historial' : 'En Cargo'}`,
      summary: [
        { label: 'Tab', value: isHist ? 'Historial' : 'En Cargo' },
        { label: 'Total registros', value: rows.length },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: isHist ? [
        { key: 'fecha', label: 'Fecha', format: (v) => v ? new Date(v).toLocaleString('es-CL') : '' },
        { key: 'tipo_movimiento', label: 'Movimiento' },
        { key: 'herramienta_codigo', label: 'Cód. Herramienta' },
        { key: 'herramienta_descripcion', label: 'Descripción' },
        { key: 'cantidad', label: 'Cantidad', format: (v) => Number(v) || 0 },
        { key: 'trabajador_nombre', label: 'Trabajador' },
        { key: 'rut_trabajador', label: 'RUT' },
        { key: 'ot_destino', label: 'OT' },
        { key: 'ubicacion', label: 'Ubicación' },
        { key: 'observaciones', label: 'Observaciones' },
        { key: 'usuario_responsable', label: 'Responsable' },
      ] : [
        { key: 'codigo', label: 'Código' },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'cantidad', label: 'Cantidad', format: (v) => Number(v) || 0 },
        { key: 'trabajador_nombre', label: 'Trabajador' },
        { key: 'rut_trabajador', label: 'RUT' },
        { key: 'ot_destino', label: 'OT' },
        { key: 'ubicacion', label: 'Ubicación' },
        { key: 'fecha_asignacion', label: 'Fecha asignación' },
        { key: 'observaciones', label: 'Observaciones' },
      ],
      rows,
    });
  };

  // Reasign trabajador search (incluye toggle inactivos y stats)
  const [reasigMostrarInactivos, setReasigMostrarInactivos] = useState(false);
  const [reasigStatsTrabs, setReasigStatsTrabs] = useState({ activos: 0, inactivos: 0 });
  useEffect(() => {
    const t = setTimeout(async () => {
      if (reasigTrabSearch.length >= 2) {
        const res = await api.get(
          `/bodega/buscar-trabajador?q=${encodeURIComponent(reasigTrabSearch)}&solo_activos=${!reasigMostrarInactivos}`,
        );
        setReasigTrabResults(res.data.trabajadores || []);
        setReasigStatsTrabs({
          activos: res.data.total_activos || 0,
          inactivos: res.data.total_inactivos_disponibles || 0,
        });
      } else {
        setReasigTrabResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [reasigTrabSearch, reasigMostrarInactivos]);

  // Asignar: toggle ver inactivos + stats
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [statsTrabs, setStatsTrabs] = useState({ activos: 0, inactivos: 0 });

  const handleReasignar = async () => {
    if (!reasigAsig) return;
    if (!reasigSelectedTrab && !reasigOt) {
      toast.error('Seleccioná nuevo trabajador o nueva OT destino');
      return;
    }
    setReasigSending(true);
    try {
      await api.post(`/bodega/herramientas/reasignar/${reasigAsig.id}`, {
        nuevo_asignado_a_rut: reasigSelectedTrab?.rut || '',
        nuevo_asignado_a_nombre: reasigSelectedTrab?.nombre || '',
        nuevo_ot_destino: reasigOt ? parseInt(reasigOt) : null,
        nueva_ubicacion: reasigUbicacion,
        observaciones: reasigObs,
      });
      toast.success(`${reasigAsig.descripcion} reasignada`);
      setReasigAsig(null);
      setReasigSelectedTrab(null); setReasigTrabSearch('');
      setReasigOt(''); setReasigObs(''); setReasigUbicacion('en_cargo');
      loadEnCargo();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reasignar');
    } finally { setReasigSending(false); }
  };

  // Cmd+K comandos contextuales
  useRegisterCmdKCommands(
    'herramientas-cargo',
    'Herramientas en Cargo',
    [
      { id: 'h-tab-cargo', label: `Ir a tab En Cargo (${enCargo.length})`, icon: 'ArrowRight',
        run: () => setTab('cargo'), enabled: tab !== 'cargo' },
      { id: 'h-tab-asignar', label: 'Ir a tab Asignar', icon: 'Plus',
        run: () => setTab('asignar'), enabled: tab !== 'asignar' },
      { id: 'h-tab-ubicacion', label: 'Ir a tab Buscar Ubicación', icon: 'Filter',
        keywords: 'donde esta buscar ubicacion',
        run: () => setTab('ubicacion'), enabled: tab !== 'ubicacion' },
      { id: 'h-tab-historial', label: 'Ir a tab Historial', icon: 'Filter',
        run: () => setTab('historial'), enabled: tab !== 'historial' },
      { id: 'h-export-csv', label: 'Exportar historial a CSV',
        sublabel: 'Respeta filtros aplicados', icon: 'Download',
        run: exportCSVHistorial, enabled: tab === 'historial' },
      { id: 'h-refresh', label: 'Refrescar listado', icon: 'RefreshCw',
        run: () => {
          if (tab === 'cargo') loadEnCargo();
          else if (tab === 'historial') loadHistorial();
        },
        enabled: tab === 'cargo' || tab === 'historial' },
    ],
    [tab, enCargo.length, loadEnCargo, loadHistorial],
  );

  // Search helpers
  useEffect(() => {
    const t = setTimeout(async () => {
      if (herramientaSearch.length >= 2) {
        const res = await api.get(`/bodega/herramientas/buscar?q=${encodeURIComponent(herramientaSearch)}`);
        setHerramientaResults(res.data.herramientas || []);
        setShowHerResults(true);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [herramientaSearch]);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (trabSearch.length >= 2) {
        const res = await api.get(
          `/bodega/buscar-trabajador?q=${encodeURIComponent(trabSearch)}&solo_activos=${!mostrarInactivos}`,
        );
        setTrabResults(res.data.trabajadores || []);
        setShowTrabResults(true);
        setStatsTrabs({
          activos: res.data.total_activos || 0,
          inactivos: res.data.total_inactivos_disponibles || 0,
        });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [trabSearch, mostrarInactivos]);

  useEffect(() => {
    const loadOts = async () => {
      const res = await api.get(`/bodega/ots-en-ejecucion${otSearch ? '?q=' + encodeURIComponent(otSearch) : ''}`);
      setOtResults(res.data.ots || []);
    };
    const t = setTimeout(loadOts, 300);
    return () => clearTimeout(t);
  }, [otSearch]);

  useEffect(() => {
    const handler = (e) => {
      if (herRef.current && !herRef.current.contains(e.target)) setShowHerResults(false);
      if (trabRef.current && !trabRef.current.contains(e.target)) setShowTrabResults(false);
      if (otRef.current && !otRef.current.contains(e.target)) setShowOtResults(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleAsignar = async () => {
    if (!selectedHerramienta) { toast.error('Seleccione una herramienta'); return; }
    if (!selectedTrab && !selectedOt) { toast.error('Seleccione trabajador u OT destino'); return; }
    setSending(true);
    try {
      await api.post('/bodega/herramientas/asignar', {
        codigo_material: selectedHerramienta.codigo,
        descripcion: selectedHerramienta.descripcion2,
        cantidad,
        asignado_a_rut: selectedTrab?.rut || '',
        asignado_a_nombre: selectedTrab?.nombre || '',
        ot_destino: parseInt(selectedOt) || 0,
        ubicacion,
        observaciones: obs,
      });
      toast.success('Herramienta asignada');
      setSelectedHerramienta(null); setHerramientaSearch('');
      setSelectedTrab(null); setTrabSearch('');
      setCantidad(1); setObs('');
      setTab('cargo');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setSending(false); }
  };

  const handleDevolver = async (id, desc) => {
    try {
      await api.post(`/bodega/herramientas/devolver/${id}`);
      toast.success(`${desc} devuelta a bodega`);
      loadEnCargo();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: '2-digit' }); }
    catch { return d; }
  };

  return (
    <div className="space-y-6" data-testid="herramientas-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wrench className="h-6 w-6" /> Herramientas en Cargo
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Control de préstamo y devolución</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant={tab === 'cargo' ? 'default' : 'outline'} onClick={() => setTab('cargo')} data-testid="tab-cargo">
            <MapPin className="h-4 w-4 mr-1" /> En Cargo ({enCargo.length})
          </Button>
          <Button variant={tab === 'asignar' ? 'default' : 'outline'} onClick={() => setTab('asignar')} data-testid="tab-asignar">
            <Plus className="h-4 w-4 mr-1" /> Asignar
          </Button>
          <Button variant={tab === 'ubicacion' ? 'default' : 'outline'} onClick={() => setTab('ubicacion')} data-testid="tab-ubicacion">
            <Search className="h-4 w-4 mr-1" /> Ubicación
          </Button>
          <Button variant={tab === 'historial' ? 'default' : 'outline'} onClick={() => setTab('historial')} data-testid="tab-historial">
            <History className="h-4 w-4 mr-1" /> Historial
          </Button>
          {(tab === 'cargo' || tab === 'historial') && (
            <>
              <Button variant="outline" size="sm" onClick={imprimirHerramientas} title="Imprimir tab actual" data-testid="btn-print-herram">
                <Printer className="h-4 w-4 mr-1" /> Imprimir
              </Button>
              <Button variant="outline" size="sm" onClick={exportarHerramientasExcel} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Exportar a Excel" data-testid="btn-excel-herram">
                <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
              </Button>
            </>
          )}
        </div>
      </div>

      {tab === 'cargo' && (
        <div className="space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Filtrar por trabajador..." value={searchFilter}
              onChange={e => setSearchFilter(e.target.value)} data-testid="filter-cargo" />
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
          ) : porPersona.length === 0 ? (
            <div className="bg-card border rounded-xl p-12 text-center text-muted-foreground">
              <Wrench className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No hay herramientas asignadas</p>
            </div>
          ) : (
            <div className="space-y-4">
              {porPersona.map((persona, pi) => (
                <div key={pi} className="bg-card border rounded-xl overflow-hidden" data-testid={`persona-${pi}`}>
                  <div className="px-4 py-3 bg-muted/50 border-b flex items-center gap-2">
                    <User className="h-4 w-4 text-primary" />
                    <span className="font-semibold text-sm">{persona.nombre}</span>
                    {persona.rut && <span className="text-xs text-muted-foreground">({persona.rut})</span>}
                    <span className="ml-auto text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{persona.herramientas.length} herramienta{persona.herramientas.length > 1 ? 's' : ''}</span>
                  </div>
                  <div className="divide-y">
                    {persona.herramientas.map((h, hi) => (
                      <div key={hi} className="px-4 py-2.5 flex items-center justify-between hover:bg-muted/30 gap-2 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{h.descripcion}</div>
                          <div className="text-xs text-muted-foreground flex gap-3 flex-wrap">
                            <span>Cant: {h.cantidad}</span>
                            <span>OT: {h.ot_destino || '-'}</span>
                            <span>Desde: {formatDate(h.fecha_asignacion)}</span>
                            {h.reasignada_desde_trabajador && (
                              <span className="text-indigo-600">← reasig. de {h.reasignada_desde_trabajador}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setReasigAsig(h);
                              setReasigUbicacion(h.ubicacion || 'en_cargo');
                            }}
                            className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                            data-testid={`reasignar-${h.id}`}
                          >
                            <ArrowLeftRight className="h-3.5 w-3.5 mr-1" /> Reasignar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDevolver(h.id, h.descripcion)}
                            className="text-emerald-600 border-emerald-300 hover:bg-emerald-50" data-testid={`devolver-${h.id}`}>
                            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Devolver
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'asignar' && (
        <div className="bg-card border rounded-xl p-6 max-w-2xl space-y-5">
          {/* Herramienta */}
          <div className="relative" ref={herRef}>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Herramienta</label>
            <div className="relative">
              <Package className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input className="pl-9" placeholder="Buscar herramienta..." value={herramientaSearch}
                onChange={e => { setHerramientaSearch(e.target.value); setSelectedHerramienta(null); }} data-testid="search-herramienta" />
            </div>
            {showHerResults && herramientaResults.length > 0 && (
              <div className="absolute z-50 top-full mt-1 w-full bg-popover border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {herramientaResults.map((m, i) => (
                  <button key={i} className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between"
                    onClick={() => { setSelectedHerramienta(m); setHerramientaSearch(m.descripcion2); setShowHerResults(false); }}>
                    <span>{m.descripcion2}</span>
                    <span className={`text-xs font-bold ${m.stock_actual > 0 ? 'text-emerald-600' : 'text-red-500'}`}>Stock: {m.stock_actual}</span>
                  </button>
                ))}
              </div>
            )}
            {selectedHerramienta && <div className="mt-1 text-xs text-emerald-600 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> {selectedHerramienta.descripcion2} (Stock: {selectedHerramienta.stock_actual})</div>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Worker */}
            <div className="relative" ref={trabRef}>
              <label className="text-xs font-medium text-muted-foreground mb-1 block flex justify-between items-center">
                <span>Asignar a (Trabajador / Usuario)</span>
                {trabSearch.length >= 2 && (
                  <span className="text-[10px] text-muted-foreground">
                    {statsTrabs.activos} activos
                    {statsTrabs.inactivos > 0 && !mostrarInactivos && (
                      <button
                        type="button"
                        onClick={() => setMostrarInactivos(true)}
                        className="ml-1 text-indigo-600 hover:underline"
                        data-testid="btn-mostrar-inactivos"
                      >
                        + {statsTrabs.inactivos} inactivos
                      </button>
                    )}
                    {mostrarInactivos && (
                      <button
                        type="button"
                        onClick={() => setMostrarInactivos(false)}
                        className="ml-1 text-indigo-600 hover:underline"
                      >
                        (ocultar inactivos)
                      </button>
                    )}
                  </span>
                )}
              </label>
              <Input placeholder="Buscar nombre o RUT..." value={trabSearch}
                onChange={e => { setTrabSearch(e.target.value); setSelectedTrab(null); }} data-testid="search-trab-hmta" />
              {showTrabResults && trabResults.length > 0 && (
                <div className="absolute z-50 top-full mt-1 w-full bg-popover border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {trabResults.map((t, i) => (
                    <button key={i} className={`w-full text-left px-3 py-1.5 hover:bg-muted text-sm flex items-center justify-between gap-2 ${t.activo === false ? 'opacity-60' : ''}`}
                      onClick={() => { setSelectedTrab(t); setTrabSearch(t.nombre); setShowTrabResults(false); }}>
                      <span>
                        {t.nombre} {t.rut && <span className="text-muted-foreground text-xs">({t.rut})</span>}
                      </span>
                      <span className="flex gap-1 items-center">
                        {t.tipo === 'usuario' ? (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium">Usuario sistema</span>
                        ) : (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">Trabajador</span>
                        )}
                        {t.activo === false && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">INACTIVO</span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* OT */}
            <div className="relative" ref={otRef}>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">OT Destino</label>
              <Input placeholder="Buscar OT..." value={otSearch}
                onChange={e => { setOtSearch(e.target.value); setSelectedOt(''); }}
                onFocus={() => setShowOtResults(true)} data-testid="ot-hmta" />
              {showOtResults && otResults.length > 0 && (
                <div className="absolute z-50 top-full mt-1 w-full bg-popover border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {otResults.filter(o => !otSearch || String(o.numero_ot).includes(otSearch)).slice(0, 10).map((o, i) => (
                    <button key={i} className="w-full text-left px-3 py-1.5 hover:bg-muted text-sm flex justify-between"
                      onClick={() => { setSelectedOt(String(o.numero_ot)); setOtSearch(`${o.numero_ot} - ${(o.descripcion||'').slice(0,25)}`); setShowOtResults(false); }}>
                      <span className="font-mono font-bold">{o.numero_ot}</span>
                      <span className="text-xs text-muted-foreground truncate ml-2">{(o.descripcion||'').slice(0,30)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Cantidad</label>
              <Input type="number" min="1" value={cantidad} onChange={e => setCantidad(Number(e.target.value))} />
            </div>
            <div className="relative" ref={ubicRef}>
              <label className="text-xs font-medium text-muted-foreground mb-1 block flex justify-between">
                <span>Ubicación</span>
                <span className="text-[10px] text-muted-foreground">{ubicaciones.length} disponibles</span>
              </label>
              <Input
                value={ubicacion}
                onChange={e => { setUbicacion(e.target.value.toUpperCase()); setShowUbicPicker(true); }}
                onFocus={() => setShowUbicPicker(true)}
                onBlur={() => setTimeout(() => setShowUbicPicker(false), 200)}
                placeholder="Ej: BODEGA, MONTAJE, EN OBRA…"
                data-testid="input-ubicacion-asignar"
              />
              {showUbicPicker && (() => {
                const qU = (ubicacion || '').trim().toUpperCase();
                const filtered = ubicaciones.filter(u => !qU || u.nombre.includes(qU));
                const exactMatch = ubicaciones.some(u => u.nombre === qU);
                return (
                  <div className="absolute z-50 top-full mt-1 w-full bg-popover border rounded-lg shadow-lg max-h-52 overflow-y-auto">
                    {filtered.length === 0 && !qU && (
                      <div className="px-3 py-2 text-xs text-muted-foreground">Sin ubicaciones</div>
                    )}
                    {filtered.slice(0, 20).map(u => (
                      <button
                        key={u.id}
                        type="button"
                        className={`w-full text-left px-3 py-1.5 text-sm hover:bg-muted ${ubicacion === u.nombre ? 'bg-indigo-50 dark:bg-indigo-900/30 font-medium' : ''}`}
                        onClick={() => { setUbicacion(u.nombre); setShowUbicPicker(false); }}
                        data-testid={`option-ubic-${u.nombre.replace(/\s+/g, '_')}`}
                      >
                        {u.nombre}
                      </button>
                    ))}
                    {qU && qU.length >= 2 && !exactMatch && (
                      <button
                        type="button"
                        disabled={creatingUbic}
                        className="w-full text-left px-3 py-2 text-sm border-t bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/30 text-emerald-700 font-medium flex items-center gap-1"
                        onClick={async () => {
                          const nuevo = await crearUbicacion(qU);
                          if (nuevo) { setUbicacion(nuevo); setShowUbicPicker(false); }
                        }}
                        data-testid="btn-crear-ubicacion-inline"
                      >
                        {creatingUbic ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                        Crear ubicación "{qU}"
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Observaciones</label>
              <Input value={obs} onChange={e => setObs(e.target.value)} placeholder="Opcional..." />
            </div>
          </div>

          <Button className="w-full" size="lg" disabled={!selectedHerramienta || sending} onClick={handleAsignar} data-testid="submit-asignar">
            <Plus className="h-4 w-4 mr-2" /> Asignar Herramienta
          </Button>
        </div>
      )}

      {tab === 'ubicacion' && (
        <div className="space-y-4" data-testid="tab-content-ubicacion">
          <div className="bg-card border rounded-xl p-4">
            <form onSubmit={(e) => { e.preventDefault(); buscarUbicacion(); }} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="¿Dónde está X herramienta? · Buscá por código o descripción"
                  value={ubicQuery}
                  onChange={e => setUbicQuery(e.target.value)}
                  autoFocus
                  data-testid="input-ubicacion-search"
                />
              </div>
              <Button type="submit" disabled={ubicLoading} data-testid="btn-ubicacion-search">
                {ubicLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                Buscar
              </Button>
            </form>
          </div>

          {ubicResultados.length > 0 && (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs text-muted-foreground">
                {ubicResultados.length} herramienta{ubicResultados.length !== 1 ? 's' : ''} encontrada{ubicResultados.length !== 1 ? 's' : ''}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => descargarQR(ubicResultados.map(r => r.codigo))}
                data-testid="btn-qr-bulk"
              >
                <Printer className="h-3.5 w-3.5 mr-1" /> Imprimir etiquetas QR ({ubicResultados.length})
              </Button>
            </div>
          )}

          {ubicSearched && !ubicLoading && ubicResultados.length === 0 && (
            <div className="bg-card border rounded-xl p-8 text-center text-muted-foreground">Sin resultados para "{ubicQuery}".</div>
          )}

          {ubicResultados.map((h) => (
            <div key={h.codigo} className="bg-card border rounded-xl p-4 space-y-2" data-testid={`ubic-card-${h.codigo}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-amber-600" /> {h.descripcion}
                    <span className="font-mono text-xs text-muted-foreground">{h.codigo}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Stock en bodega: <strong>{h.stock_en_bodega}</strong> {h.unidad} ·
                    En cargo: <strong className="text-amber-700">{h.total_en_cargo}</strong> {h.unidad}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => descargarQR(h.codigo)}
                  className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                  data-testid={`btn-qr-${h.codigo}`}
                  title="Descargar etiqueta QR"
                >
                  <QrCode className="h-3.5 w-3.5 mr-1" /> QR
                </Button>
              </div>
              {h.asignaciones_activas.length > 0 ? (
                <div className="border-t pt-2">
                  <div className="text-xs font-semibold text-muted-foreground mb-1">EN CARGO:</div>
                  <div className="space-y-1">
                    {h.asignaciones_activas.map((a, i) => (
                      <div key={i} className="text-xs bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded px-2.5 py-1.5 flex items-center justify-between gap-2">
                        <div>
                          <strong>{a.asignado_a_nombre || `OT ${a.ot_destino}`}</strong>
                          {a.asignado_a_rut && <span className="text-muted-foreground"> · {a.asignado_a_rut}</span>}
                          <span className="text-muted-foreground"> · {a.cantidad} {a.unidad}</span>
                          <span className="text-muted-foreground"> · Desde {formatDate(a.fecha_asignacion)}</span>
                          {a.ubicacion && <span className="ml-1 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-[10px]">{a.ubicacion}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="border-t pt-2 text-xs text-emerald-700">✓ Disponible en bodega</div>
              )}
              {h.historial_reciente && h.historial_reciente.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Últimos movimientos ({h.historial_reciente.length})</summary>
                  <div className="mt-1 space-y-0.5 pl-3">
                    {h.historial_reciente.map((m, i) => (
                      <div key={i} className="text-xs text-muted-foreground">
                        <span className="font-mono">{formatDate(m.fecha_asignacion)}</span> · {m.asignado_a_nombre || `OT ${m.ot_destino}`} · <span className={m.estado === 'asignada' ? 'text-amber-600' : m.estado === 'reasignada' ? 'text-indigo-600' : 'text-emerald-600'}>{m.estado}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'historial' && (
        <div className="space-y-3">
          {/* Filtros */}
          <div className="bg-card border rounded-xl p-3 grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="historial-filtros">
            <div>
              <label className="text-[10px] font-medium text-muted-foreground block">Desde</label>
              <Input type="date" value={hFechaDesde} onChange={e => setHFechaDesde(e.target.value)} className="h-8 text-xs" data-testid="h-fecha-desde" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-muted-foreground block">Hasta</label>
              <Input type="date" value={hFechaHasta} onChange={e => setHFechaHasta(e.target.value)} className="h-8 text-xs" data-testid="h-fecha-hasta" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-muted-foreground block">Trabajador</label>
              <Input placeholder="Nombre o RUT" value={hTrabajador} onChange={e => setHTrabajador(e.target.value)} className="h-8 text-xs" data-testid="h-trab" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-muted-foreground block">OT</label>
              <Input type="number" value={hOt} onChange={e => setHOt(e.target.value)} className="h-8 text-xs" data-testid="h-ot" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-muted-foreground block">Código</label>
              <Input value={hCodigo} onChange={e => setHCodigo(e.target.value)} className="h-8 text-xs" data-testid="h-codigo" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-muted-foreground block">Estado</label>
              <select className="w-full h-8 rounded-md border bg-background px-2 text-xs" value={hEstado} onChange={e => setHEstado(e.target.value)} data-testid="h-estado">
                <option value="">Todos</option>
                <option value="asignada">En Cargo</option>
                <option value="devuelta">Devuelta</option>
                <option value="reasignada">Reasignada</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={loadHistorial} data-testid="btn-filtrar-historial">
              <Filter className="h-3.5 w-3.5 mr-1" /> Aplicar filtros
            </Button>
            <Button size="sm" variant="outline" onClick={exportCSVHistorial} data-testid="btn-export-csv">
              <Download className="h-3.5 w-3.5 mr-1" /> Exportar CSV
            </Button>
            <Button size="sm" variant="ghost" onClick={() => {
              setHFechaDesde(''); setHFechaHasta(''); setHTrabajador('');
              setHOt(''); setHCodigo(''); setHEstado('');
              setTimeout(loadHistorial, 0);
            }}>
              Limpiar
            </Button>
            <span className="ml-auto text-xs text-muted-foreground self-center">{historial.length} movimientos</span>
          </div>

          <div className="bg-card border rounded-xl overflow-hidden">
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : historial.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground text-sm">Sin registros (probá ajustar los filtros)</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="historial-hmta-table">
                  <thead><tr className="border-b bg-muted/50">
                    <th className="text-left px-3 py-2 font-medium text-xs">Fecha Asig.</th>
                    <th className="text-left px-3 py-2 font-medium text-xs">Código</th>
                    <th className="text-left px-3 py-2 font-medium text-xs">Herramienta</th>
                    <th className="text-right px-3 py-2 font-medium text-xs">Cant.</th>
                    <th className="text-left px-3 py-2 font-medium text-xs">Asignado a</th>
                    <th className="text-left px-3 py-2 font-medium text-xs">OT</th>
                    <th className="text-left px-3 py-2 font-medium text-xs">Ubicación</th>
                    <th className="text-left px-3 py-2 font-medium text-xs">Estado</th>
                    <th className="text-left px-3 py-2 font-medium text-xs">Fecha Dev.</th>
                  </tr></thead>
                  <tbody>
                    {historial.map((h, idx) => (
                      <tr key={idx} className="border-b hover:bg-muted/30">
                        <td className="px-3 py-2 text-xs font-mono">{formatDate(h.fecha_asignacion)}</td>
                        <td className="px-3 py-2 text-xs font-mono">{h.codigo_material}</td>
                        <td className="px-3 py-2 text-xs">{h.descripcion}</td>
                        <td className="px-3 py-2 text-xs text-right">{h.cantidad}</td>
                        <td className="px-3 py-2 text-xs">{h.asignado_a_nombre || '-'}</td>
                        <td className="px-3 py-2 text-xs">{h.ot_destino || '-'}</td>
                        <td className="px-3 py-2 text-xs">{h.ubicacion || '-'}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            h.estado === 'asignada' ? 'bg-amber-100 text-amber-800' :
                            h.estado === 'reasignada' ? 'bg-indigo-100 text-indigo-800' :
                            'bg-emerald-100 text-emerald-800'
                          }`}>{h.estado === 'asignada' ? 'En Cargo' : h.estado === 'reasignada' ? 'Reasignada' : 'Devuelta'}</span>
                        </td>
                        <td className="px-3 py-2 text-xs">{formatDate(h.fecha_devolucion)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Reasignar */}
      <Dialog open={!!reasigAsig} onOpenChange={(o) => !o && setReasigAsig(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-reasignar-herramienta">
          {reasigAsig && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ArrowLeftRight className="w-5 h-5 text-indigo-600" /> Re-asignar herramienta
                </DialogTitle>
                <DialogDescription>
                  Transferir directamente sin pasar por bodega.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="bg-slate-50 dark:bg-slate-900 border rounded p-3 text-xs space-y-1">
                  <div><strong>{reasigAsig.descripcion}</strong> · cantidad {reasigAsig.cantidad}</div>
                  <div className="text-muted-foreground">
                    De: <strong>{reasigAsig.asignado_a_nombre || `OT ${reasigAsig.ot_destino || '-'}`}</strong>
                  </div>
                </div>
                <div className="relative">
                  <label className="text-xs font-medium block mb-1">Nuevo trabajador</label>
                  <Input
                    placeholder="Buscar nombre o RUT…"
                    value={reasigTrabSearch}
                    onChange={e => { setReasigTrabSearch(e.target.value); setReasigSelectedTrab(null); }}
                    data-testid="input-reasig-trab"
                  />
                  {reasigTrabSearch.length >= 2 && reasigTrabResults.length > 0 && !reasigSelectedTrab && (
                    <div className="absolute z-50 top-full mt-1 w-full bg-popover border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                      {reasigTrabResults.map((t, i) => (
                        <button key={i} type="button" className="w-full text-left px-3 py-1.5 hover:bg-muted text-sm"
                          onClick={() => { setReasigSelectedTrab(t); setReasigTrabSearch(t.nombre); }}>
                          {t.nombre} <span className="text-muted-foreground text-xs">({t.rut})</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium block mb-1">OT destino</label>
                    <Input type="number" placeholder="Opcional" value={reasigOt} onChange={e => setReasigOt(e.target.value)} data-testid="input-reasig-ot" />
                  </div>
                  <div>
                    <label className="text-xs font-medium block mb-1">Ubicación</label>
                    <select
                      className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                      value={reasigUbicacion}
                      onChange={e => setReasigUbicacion(e.target.value)}
                      data-testid="select-reasig-ubicacion"
                    >
                      {ubicaciones.map(u => (
                        <option key={u.id} value={u.nombre}>{u.nombre}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1">Observaciones</label>
                  <Input value={reasigObs} onChange={e => setReasigObs(e.target.value)} placeholder="Motivo (opcional)…" data-testid="input-reasig-obs" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setReasigAsig(null)} disabled={reasigSending}>Cancelar</Button>
                <Button
                  onClick={handleReasignar}
                  disabled={reasigSending || (!reasigSelectedTrab && !reasigOt)}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="btn-confirmar-reasignar"
                >
                  {reasigSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Reasignar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
